// 🛡️ detectorContraditorios — port FRONT (B223-FU, AI_116) do módulo puro de
// AI_40 Patch 2 (antes em api/_lib/detectorContraditorios.js, hoje removido).
// ─────────────────────────────────────────────────────────────────────────────
// Detecta sinais de hepatopatia avançada / hipertensão portal nos DADOS-FONTE do
// paciente (imagem, biópsia, EDA, plaquetas). É PURO e DETERMINÍSTICO (sem IA).
// Movido para o front porque a análise geral mantém os dados-fonte estruturados
// NO NAVEGADOR (privacidade): só texto minimizado vai ao backend. Rodar aqui
// significa ZERO dado novo em trânsito (LGPD) e sinal completo (texto não
// truncado). Os avisos resultantes alimentam a caixa "Pontos a conferir" (B223).
//
// Lógica idêntica à versão original (provada por detectorContraditorios.test.ts,
// porte 1:1 dos 18 testes do antigo .test.mjs). `normalizar` foi inlinado para o
// módulo ser autossuficiente (não arrastar adequacaoAmostral).

export type Severidade = "forte" | "moderada";

export interface AchadoContraditorio {
  rotulo: string;
  severidade: Severidade;
  fonte?: string;
}

export interface Contraditorio extends AchadoContraditorio {
  gatilho: number;
}

export interface ResumoContraditorios {
  qtd: number;
  temForte: boolean;
  temModerado: boolean;
  rebaixarBiopsiaMarginal: boolean;
}

export interface ResultadoContexto {
  contraditorios: Contraditorio[];
  resumo: ResumoContraditorios;
  avaliadoEm: string;
}

export interface CtxContraditorio {
  biopsiaAtualId?: string | null;
  examesImagem?: any[];
  outrasBiopsias?: any[];
  citacoesHistopato?: any[];
  edas?: any[];
  examesLaboratoriais?: any[];
}

// Normalizador (NFD/minúsculas/trim) — inline (era importado de adequacaoAmostral).
export function normalizar(texto: unknown): string {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

// ---------------------------------------------------------------------------
// HELPER — Guarda contra falso positivo por negação contextual
// ---------------------------------------------------------------------------
const PARTICULAS_NEGACAO =
  /\b(sem(\s+sinais?|\s+evid[eê]ncia)?|aus[eê]ncia\s+de|n[aã]o\s+(h[aá]|houve|apresenta|identifica|observa|foram?)|nega[-\s]?se|exclui[-\s]?se|descart[ao])\b/i;

// B210 (AI_93) — a janela de negação NÃO pode cruzar o fim da frase.
const TERMINADOR_FRASE = /[.;\n]/g;

export function temNegacaoAntes(texto: string, indiceMatch: number, janela = 40): boolean {
  if (indiceMatch == null || indiceMatch < 0) return false;
  const inicio = Math.max(0, indiceMatch - janela);
  let contextoAntes = texto.substring(inicio, indiceMatch);
  let limite = -1;
  let m: RegExpExecArray | null;
  TERMINADOR_FRASE.lastIndex = 0;
  while ((m = TERMINADOR_FRASE.exec(contextoAntes)) !== null) {
    limite = m.index;
  }
  if (limite >= 0) contextoAntes = contextoAntes.substring(limite + 1);
  return PARTICULAS_NEGACAO.test(contextoAntes);
}

// ---------------------------------------------------------------------------
// GATILHO 1 — Imagem com sinais morfológicos de hepatopatia avançada
// ---------------------------------------------------------------------------
const TERMOS_MORFOLOGICOS_HEPATOPATIA_AVANCADA: {
  regex: RegExp;
  rotulo: string;
  severidade: Severidade;
}[] = [
  { regex: /\besplenomegalia\b/, rotulo: "esplenomegalia", severidade: "forte" },
  {
    regex: /\bba[cç]o\b[^.]{0,40}\b(aumentad|volumos|cm)\b/,
    rotulo: "baço aumentado por medida",
    severidade: "moderada",
  },
  {
    regex: /contorn[oa]s?[^.]{0,30}\b(lobulad|irregular|bossel|nodular|rombud)/,
    rotulo: "contornos hepáticos irregulares/lobulados",
    severidade: "forte",
  },
  {
    regex: /circula[cç][aã]o\s+colateral/,
    rotulo: "circulação colateral",
    severidade: "forte",
  },
  {
    regex: /colaterais?\s+portossist[eê]micas?/,
    rotulo: "colaterais portossistêmicas",
    severidade: "forte",
  },
  {
    regex: /shunts?\s+portossist[eê]micos?/,
    rotulo: "shunts portossistêmicos",
    severidade: "forte",
  },
  {
    regex: /varizes?\s+(esof[aá]gic|g[aá]stric|periespl[eê]nic|perig[aá]stric)/,
    rotulo: "varizes em imagem",
    severidade: "forte",
  },
  { regex: /\bascite\b/, rotulo: "ascite", severidade: "forte" },
  {
    regex: /recanaliza[cç][aã]o\s+(da\s+)?veia\s+umbilical/,
    rotulo: "recanalização veia umbilical",
    severidade: "forte",
  },
  {
    regex: /hipertens[aã]o\s+portal/,
    rotulo: "hipertensão portal (textual)",
    severidade: "forte",
  },
  { regex: /cirrose\b/, rotulo: "cirrose (textual em imagem)", severidade: "forte" },
  {
    regex: /\bhepatopatia\s+cr[oô]nica\s+avan[cç]ada\b/,
    rotulo: "hepatopatia crônica avançada",
    severidade: "forte",
  },
];

const REGEX_ELASTO_KPA = /(\d+[,.]\d+|\d+)\s*kpa/i;
const REGEX_ELASTO_FIB = /\bf[34]\b/i;

export function analisarImagem(
  exameLaudoTexto: unknown,
  tipoExame: unknown
): AchadoContraditorio[] {
  const t = normalizar(exameLaudoTexto);
  const achados: AchadoContraditorio[] = [];

  for (const { regex, rotulo, severidade } of TERMOS_MORFOLOGICOS_HEPATOPATIA_AVANCADA) {
    const regexGlobal = new RegExp(
      regex.source,
      regex.flags.includes("g") ? regex.flags : regex.flags + "g"
    );
    let m: RegExpExecArray | null;
    let jaAdicionado = false;
    while ((m = regexGlobal.exec(t)) !== null) {
      if (!temNegacaoAntes(t, m.index)) {
        if (!jaAdicionado) {
          achados.push({ rotulo, severidade });
          jaAdicionado = true;
        }
      }
    }
  }

  const tipoNorm = normalizar(tipoExame);
  if (tipoNorm.includes("elastografia") || t.includes("fibroscan") || t.includes("arfi")) {
    const mKpa = t.match(REGEX_ELASTO_KPA);
    if (mKpa) {
      const kpa = parseFloat(mKpa[1].replace(",", "."));
      if (kpa >= 10) {
        achados.push({ rotulo: `elastografia ≥10 kPa (${kpa})`, severidade: "forte" });
      }
    }
    if (REGEX_ELASTO_FIB.test(t)) {
      achados.push({ rotulo: "F3/F4 em elastografia (textual)", severidade: "forte" });
    }
  }

  return achados;
}

// ---------------------------------------------------------------------------
// GATILHO 2 — Biópsia anterior com fibrose maior
// ---------------------------------------------------------------------------
const REGEX_F_METAVIR = /\bmetavir[^.\n]{0,50}?f\s*([0-4])/i;
const REGEX_F_SOLTO = /\bf\s*([0-4])\b/i;
const REGEX_ISHAK = /ishak[^.\n]{0,30}?(\d+)/i;
const REGEX_CIRROSE = /\bcirrose\b/i;

export function extrairFibroseDeTexto(
  texto: unknown
): { escalaScore: number; escala: string } | null {
  const t = normalizar(texto);
  const mMeta = t.match(REGEX_F_METAVIR);
  if (mMeta) return { escalaScore: parseInt(mMeta[1], 10), escala: "metavir" };
  const mF = t.match(REGEX_F_SOLTO);
  if (mF) return { escalaScore: parseInt(mF[1], 10), escala: "metavir" };
  const mIshak = t.match(REGEX_ISHAK);
  if (mIshak) return { escalaScore: parseInt(mIshak[1], 10), escala: "ishak" };
  if (REGEX_CIRROSE.test(t)) return { escalaScore: 4, escala: "textual_cirrose" };
  return null;
}

export function analisarBiopsiasAnteriores(
  biopsiaAtualId: string | null | undefined,
  outrasBiopsias: any[],
  citacoesHistopato: any[]
): AchadoContraditorio[] {
  const achados: AchadoContraditorio[] = [];

  for (const b of outrasBiopsias || []) {
    if (b.id === biopsiaAtualId) continue;
    const textoBase =
      b.analiseCompleta || b.laudo || b.achados?.conclusaoLaudistaLiteral || "";
    const f = extrairFibroseDeTexto(textoBase);
    if (!f) continue;

    const ehAlta =
      (f.escala === "metavir" && f.escalaScore >= 2) ||
      (f.escala === "ishak" && f.escalaScore >= 3) ||
      f.escala === "textual_cirrose";

    if (ehAlta) {
      achados.push({
        rotulo: `biópsia anterior (${
          b.dataExame || "data desconhecida"
        }) com ${f.escala.toUpperCase()} ${f.escalaScore}`,
        severidade: "forte",
        fonte: "biopsia_anterior_estruturada",
      });
    }
  }

  for (const cit of citacoesHistopato || []) {
    const f = extrairFibroseDeTexto(cit.texto || "");
    if (!f) continue;
    const ehAlta =
      (f.escala === "metavir" && f.escalaScore >= 2) ||
      (f.escala === "ishak" && f.escalaScore >= 3) ||
      f.escala === "textual_cirrose";
    if (ehAlta) {
      achados.push({
        rotulo: `biópsia citada em consulta (${
          cit.data || "data desconhecida"
        }) com ${f.escala.toUpperCase()} ${f.escalaScore}`,
        severidade: "forte",
        fonte: "citacao_consulta",
      });
    }
  }

  return achados;
}

// ---------------------------------------------------------------------------
// GATILHO 3 — EDA com sinais de hipertensão portal
// ---------------------------------------------------------------------------
const REGEX_VARIZES_ESOFAGO = /varizes?\s+esof[aá]gic[ao]s?[^.]{0,30}\bf\s*[1-4]\b/i;
const REGEX_VARIZES_ESOF_SIMPLES = /varizes?\s+esof[aá]gic[ao]s?/i;
const REGEX_VARIZES_GASTRICAS = /varizes?\s+g[aá]stric[ao]s?/i;
const REGEX_GHP = /gastropatia\s+(hipertensiva\s+portal|congestiva)/i;

export function analisarEDA(laudoTexto: unknown): AchadoContraditorio[] {
  const t = normalizar(laudoTexto);
  const achados: AchadoContraditorio[] = [];

  const mF = t.match(REGEX_VARIZES_ESOFAGO);
  if (mF && !temNegacaoAntes(t, mF.index!)) {
    achados.push({ rotulo: "varizes esofágicas F1+ (EDA)", severidade: "forte" });
  } else {
    const mS = t.match(REGEX_VARIZES_ESOF_SIMPLES);
    if (mS && !temNegacaoAntes(t, mS.index!)) {
      achados.push({
        rotulo: "varizes esofágicas (EDA, grau não especificado)",
        severidade: "moderada",
      });
    }
  }
  const mG = t.match(REGEX_VARIZES_GASTRICAS);
  if (mG && !temNegacaoAntes(t, mG.index!)) {
    achados.push({ rotulo: "varizes gástricas (EDA)", severidade: "forte" });
  }
  const mGhp = t.match(REGEX_GHP);
  if (mGhp && !temNegacaoAntes(t, mGhp.index!)) {
    achados.push({ rotulo: "gastropatia hipertensiva portal (EDA)", severidade: "forte" });
  }
  return achados;
}

// ---------------------------------------------------------------------------
// GATILHO 4 — Plaquetopenia persistente (>=2 medidas <150.000)
// ---------------------------------------------------------------------------
export function analisarPlaquetas(examesLaboratoriais: any[]): AchadoContraditorio[] {
  if (!Array.isArray(examesLaboratoriais) || examesLaboratoriais.length === 0) {
    return [];
  }
  const medidasBaixas = examesLaboratoriais
    .map((e) => {
      const p = Number(e.plaquetas);
      return { valor: isFinite(p) ? p : null, data: e.dataExame || e.data || null };
    })
    .filter((x) => x.valor != null && (x.valor as number) < 150000);

  if (medidasBaixas.length >= 2) {
    const menor = medidasBaixas.reduce((a, b) => ((a.valor as number) < (b.valor as number) ? a : b));
    return [
      {
        rotulo: `plaquetopenia persistente (${medidasBaixas.length} medidas <150 mil, menor: ${menor.valor})`,
        severidade: "moderada",
      },
    ];
  }
  return [];
}

// ---------------------------------------------------------------------------
// ORQUESTRADOR — avalia contexto completo do paciente
// ---------------------------------------------------------------------------
export function avaliarContexto(ctx: CtxContraditorio): ResultadoContexto {
  const {
    biopsiaAtualId,
    examesImagem = [],
    outrasBiopsias = [],
    citacoesHistopato = [],
    edas = [],
    examesLaboratoriais = [],
  } = ctx || {};

  const contraditorios: Contraditorio[] = [];

  // Gatilho 1
  for (const ex of examesImagem) {
    const texto = ex.laudo || ex.analiseCompleta || ex.achados?.conclusaoLaudistaLiteral || "";
    const tipo = ex.tipoExame || ex.tipo || "";
    const achados = analisarImagem(texto, tipo);
    for (const a of achados) {
      contraditorios.push({
        gatilho: 1,
        fonte: `imagem: ${tipo} (${ex.dataExame || "data desconhecida"})`,
        ...a,
      });
    }
  }

  // Gatilho 2
  const biopsias = analisarBiopsiasAnteriores(biopsiaAtualId, outrasBiopsias, citacoesHistopato);
  for (const a of biopsias) {
    contraditorios.push({ gatilho: 2, ...a });
  }

  // Gatilho 3
  for (const ex of edas) {
    const texto = ex.laudo || ex.analiseCompleta || "";
    const achados = analisarEDA(texto);
    for (const a of achados) {
      contraditorios.push({
        gatilho: 3,
        fonte: `EDA (${ex.dataExame || "data desconhecida"})`,
        ...a,
      });
    }
  }

  // Gatilho 4
  const plaq = analisarPlaquetas(examesLaboratoriais);
  for (const a of plaq) {
    contraditorios.push({ gatilho: 4, fonte: "exames laboratoriais", ...a });
  }

  const temForte = contraditorios.some((c) => c.severidade === "forte");
  const temModerado = contraditorios.some((c) => c.severidade === "moderada");

  return {
    contraditorios,
    resumo: {
      qtd: contraditorios.length,
      temForte,
      temModerado,
      rebaixarBiopsiaMarginal: temForte, // regra: só forte rebaixa
    },
    avaliadoEm: new Date().toISOString(),
  };
}
